import React from "react";
import { RotateCcw, Truck } from "lucide-react";

import ResultsHeaderLoadingRail from "./ResultsHeaderLoadingRail.jsx";
import { joinClasses, sectionTagClassName } from "./ResultsPrimitives.jsx";

function PreviewPanel({
  animationKey = 0,
  children,
  description,
  title,
}) {
  return (
    <section className="grid gap-3 rounded-[20px] border border-black/6 bg-white px-4 py-4 shadow-[0_8px_20px_rgba(15,23,42,0.045)]">
      <div className="grid gap-1">
        <h2 className="m-0 text-[0.98rem] font-semibold tracking-[-0.02em] text-slate-950">{title}</h2>
        <p className="m-0 text-[0.84rem] leading-6 text-slate-600">{description}</p>
      </div>
      <div
        key={animationKey}
        className="rounded-[16px] border border-[rgba(15,92,87,0.12)] bg-[linear-gradient(180deg,rgba(248,250,248,0.98),rgba(255,255,255,0.98))] px-4 py-5"
        data-app-shell="true"
      >
        {children}
      </div>
    </section>
  );
}

export default function LoadingRailPreviewPage() {
  const [animationKey, setAnimationKey] = React.useState(0);

  const replayAnimation = React.useCallback(() => {
    setAnimationKey((previousKey) => previousKey + 1);
  }, []);

  return (
    <div className="mx-auto grid w-full max-w-264 gap-4 px-4 pb-6 pt-0 max-[720px]:gap-3 max-[720px]:px-4">
      <section className="rounded-[20px] border border-black/6 bg-white px-4 py-4 shadow-[0_8px_20px_rgba(15,23,42,0.045)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid min-w-0 gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className={joinClasses([sectionTagClassName, "px-2 py-0.75 text-[0.62rem]"])}>Aperçu UI</span>
              <p className="m-0 text-[0.98rem] font-semibold tracking-[-0.02em] text-slate-950">
                Animation de chargement du camion
              </p>
            </div>
            <p className="m-0 max-w-160 text-[0.84rem] leading-6 text-slate-600">
              Aperçu du camion à remorque sur une ligne épurée, avec ses roues animées et un déplacement vers la droite.
            </p>
          </div>
          <button
            type="button"
            className="inline-flex min-h-9 items-center gap-2 rounded-[10px] border border-slate-300 bg-white px-3 py-2 text-[0.78rem] font-semibold text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] transition hover:border-slate-400 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300/60"
            onClick={replayAnimation}
          >
            <RotateCcw size={14} aria-hidden="true" />
            Relancer l&apos;animation
          </button>
        </div>
      </section>

      <PreviewPanel
        animationKey={animationKey}
        title="Comme dans la barre du haut"
        description="Reproduction de la piste affichée à côté du titre de page pendant une navigation."
      >
        <div className="flex min-w-0 items-center gap-2">
          <span className="inline-flex h-7 items-center gap-1.5" aria-hidden="true">
            <span className="h-4.5 w-0.75 rounded-full bg-kpi-accent" />
            <span className="grid gap-1.25">
              <span className="h-0.75 w-8 rounded-full bg-shell-header-accent-rail-strong" />
              <span className="h-0.75 w-5 rounded-full bg-shell-header-accent-dot-strong" />
            </span>
          </span>
          <div className="flex min-w-0 items-center gap-2">
            <h1 className="m-0 truncate text-[1.1rem] font-semibold tracking-[-0.03em] text-slate-950">
              Rapports
            </h1>
            <ResultsHeaderLoadingRail alwaysVisible className="w-[clamp(10rem,28vw,18rem)]" />
          </div>
        </div>
      </PreviewPanel>

      <PreviewPanel
        animationKey={animationKey}
        title="Piste élargie"
        description="Vue agrandie pour mieux suivre le camion sur toute la route."
      >
        <div className="grid gap-3">
          <div className="flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-slate-500">
            <Truck size={14} strokeWidth={2.4} aria-hidden="true" />
            Route de dispatch
          </div>
          <ResultsHeaderLoadingRail
            alwaysVisible
            className="w-full max-w-none"
            scale="large"
          />
        </div>
      </PreviewPanel>
    </div>
  );
}
